import { useNavigate } from "react-router-dom";
import { ArrowLeft, ExternalLink, Heart, Share2 } from "lucide-react";
import { toast } from "sonner";
import { Button as TamaguiButton, XStack, YStack } from "tamagui";

import { KINDRED_SUPPORT_EMAIL } from "../../shared/support";
import {
  SUPPORT_KINDRED_EMAIL_LINK,
  SUPPORT_KINDRED_FACEBOOK_URL,
  SUPPORT_KINDRED_GOFUNDME_URL,
  SUPPORT_KINDRED_PAGE,
  SUPPORT_KINDRED_SHARE_URL,
} from "../../shared/support";
import {
  AppButton,
  AppButtonRow,
  AppCard,
  AppCardBody,
  AppSectionHeader,
  AppText,
  kindredFontFamilies,
} from "../../ui-foundation";

const SupportKindred = () => {
  const navigate = useNavigate();

  const openExternal = (url: string) => {
    window.open(url, "_blank", "noopener,noreferrer");
  };

  const handleShareKindred = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: SUPPORT_KINDRED_PAGE.featureTitle,
          text: SUPPORT_KINDRED_PAGE.featureDescription,
          url: SUPPORT_KINDRED_SHARE_URL,
        });
      } catch {
        return;
      }
      return;
    }

    if (navigator.clipboard) {
      await navigator.clipboard.writeText(SUPPORT_KINDRED_SHARE_URL);
      toast.success("Kindred link copied");
      return;
    }

    window.prompt("Copy this link", SUPPORT_KINDRED_SHARE_URL);
  };

  return (
    <YStack gap="$md" pt="$xs" pb="$md">
      <TamaguiButton
        unstyled
        type="button"
        onPress={() => navigate("/my-kindred")}
        alignSelf="flex-start"
        pressStyle={{ opacity: 0.85 }}
      >
        <XStack alignItems="center" gap={6}>
          <ArrowLeft size={14} className="text-primary" />
          <AppText color="$primary" fontFamily={kindredFontFamilies.sansBold} fontSize="$sm" lineHeight="$sm">
            Back to Menu
          </AppText>
        </XStack>
      </TamaguiButton>

      <AppCard backgroundColor="$primarySoft" borderColor="$primary">
        <AppCardBody gap="$md">
          <XStack alignItems="flex-start" gap="$sm">
            <XStack
              width={40}
              height={40}
              alignItems="center"
              justifyContent="center"
              borderRadius="$pill"
              backgroundColor="$background"
            >
              <Heart size={18} className="text-primary" />
            </XStack>
            <YStack flex={1} minWidth={0} gap="$xs">
              <AppSectionHeader title={SUPPORT_KINDRED_PAGE.featureTitle} subtitle={SUPPORT_KINDRED_PAGE.pageSubtitle} />
              <AppText color="$primary" fontSize="$xs" lineHeight="$sm" fontFamily={kindredFontFamilies.sansBold}>
                {SUPPORT_KINDRED_PAGE.pageMeta}
              </AppText>
            </YStack>
          </XStack>

          <YStack gap="$xs">
            {SUPPORT_KINDRED_PAGE.intro.map((paragraph) => (
              <AppText key={paragraph} color="$colorMuted" fontSize="$sm" lineHeight="$md">
                {paragraph}
              </AppText>
            ))}
          </YStack>

          <AppButtonRow flexWrap="wrap">
            <AppButton type="button" tone="primary" onPress={() => openExternal(SUPPORT_KINDRED_GOFUNDME_URL)}>
              <Heart size={16} />
              Support on GoFundMe
            </AppButton>
            <AppButton type="button" tone="outline" onPress={() => openExternal(SUPPORT_KINDRED_FACEBOOK_URL)}>
              <ExternalLink size={16} />
              Open Facebook Page
            </AppButton>
            <AppButton type="button" tone="outline" onPress={handleShareKindred}>
              <Share2 size={16} />
              Share Kindred
            </AppButton>
          </AppButtonRow>

          <YStack gap="$xs">
            <AppText color="$colorMuted" fontSize="$sm" lineHeight="$md">
              Interested in helping with social media promotion? Email Hannah at{" "}
              <a href={SUPPORT_KINDRED_EMAIL_LINK} className="kindred-inline-link">
                {KINDRED_SUPPORT_EMAIL}
              </a>
            </AppText>
            <AppText color="$colorMuted" fontSize="$sm" lineHeight="$md">
              Interested in being a moderator? Email Hannah at{" "}
              <a href={SUPPORT_KINDRED_EMAIL_LINK} className="kindred-inline-link">
                {KINDRED_SUPPORT_EMAIL}
              </a>
            </AppText>
            <AppText color="$colorMuted" fontSize="$sm" lineHeight="$md">
              {SUPPORT_KINDRED_PAGE.sharePrompt}
            </AppText>
          </YStack>

          <XStack
            alignSelf="flex-start"
            borderRadius="$pill"
            backgroundColor="$background"
            paddingHorizontal={12}
            paddingVertical={6}
          >
            <AppText color="$primary" fontSize="$xs" lineHeight="$sm" fontFamily={kindredFontFamilies.sansBold}>
              {SUPPORT_KINDRED_PAGE.thankYou}
            </AppText>
          </XStack>
        </AppCardBody>
      </AppCard>
    </YStack>
  );
};

export default SupportKindred;
